import { useState, useEffect, useRef, useCallback } from 'react'
import { Reference } from '@apollo/client'
import { Redirect } from 'react-router-dom'

import { useDeletePlanMutation, usePlansByOwnerQuery } from '../../../../generated/graphql'
import { ConfirmReplacePrimaryModal } from '..'
import ConfirmDeletePlanModal from './ConfirmDeletePlanModal'
import useModal from '../../../../lib/hooks/useModal'
import useNotification from '../../../../lib/hooks/useNotification'
import { useAuth } from '../../../provider/OIDCProvider'

type ConfirmDeletePlanModalWrapperProps = {
  /** ID of Plan */
  id: string
  /** Determines visibility of modal */
  isOpen: boolean
  /** Owner AppUserId. Used by Student Overview Plans */
  ownerAppUserId: number
}

const ConfirmDeletePlanModalWrapper = ({
  id,
  isOpen,
  ownerAppUserId
}: ConfirmDeletePlanModalWrapperProps) => {
  const { user } = useAuth()
  const plansByOwnerQuery = usePlansByOwnerQuery({
    variables: {
      ownerAppUserId
    }
  })
  const { hideModal } = useModal()
  const { showErrorNotif } = useNotification()
  const [newPrimaryPlanId, setNewPrimaryPlanId] = useState<string | undefined>(undefined)
  const triggerElementRef = useRef<HTMLElement | null>(null)

  // Store the element that had focus when modal opens
  useEffect(() => {
    if (isOpen) {
      // Store current focused element
      if (document.activeElement instanceof HTMLElement) {
        triggerElementRef.current = document.activeElement
      }
      // Also try to find the Delete Plan button by its aria-label as backup
      if (!triggerElementRef.current || triggerElementRef.current === document.body) {
        const deletePlanButton = document.querySelector('[aria-label*="Delete Plan"]')
        if (deletePlanButton instanceof HTMLElement) {
          triggerElementRef.current = deletePlanButton
        }
      }
    }
  }, [isOpen])

  // Restore focus to the trigger element
  const restoreFocus = useCallback(() => {
    setTimeout(() => {
      if (triggerElementRef.current && document.body.contains(triggerElementRef.current)) {
        triggerElementRef.current.focus()
      } else {
        // Fallback: try to find Delete Plan button
        const deletePlanButton = document.querySelector(
          '[aria-label*="Delete Plan"]'
        ) as HTMLElement
        if (deletePlanButton) {
          deletePlanButton.focus()
        }
      }
    }, 100)
  }, [])

  const [deletePlan, mutation] = useDeletePlanMutation({
    variables: { input: id },
    onError: () => {
      showErrorNotif('Plan could not be deleted.', deletePlan)
    },
    update(cache, { data }) {
      cache.modify({
        id: 'ROOT_QUERY',
        fields: {
          plans(plans: Reference[], { readField }) {
            return plans.filter(
              (plan) =>
                // remove deleted plan
                readField('id', plan) !== data?.deletePlan.id
            )
          }
        }
      })

      if (newPrimaryPlanId) {
        // update the new primary plan if selected
        cache.modify({
          id: `Plan:${newPrimaryPlanId}`,
          fields: {
            isPrimary() {
              return true
            }
          }
        })

        // update the primaryPlan singleton
        cache.modify({
          id: 'ROOT_QUERY',
          fields: {
            primaryPlan(_old, { toReference }) {
              return toReference(newPrimaryPlanId)
            }
          }
        })
      } else {
        // If the last plan was deleted, remove the reference to the old primary plan
        cache.modify({
          id: 'ROOT_QUERY',
          fields: {
            primaryPlan(_old, { toReference }) {
              return toReference('')
            }
          }
        })
      }
    },
    optimisticResponse: {
      __typename: 'Mutation',
      deletePlan: {
        __typename: 'ResponseStatus',
        id,
        message: '',
        success: true
      }
    }
  })
  const onConfirm = () => {
    hideModal()
    deletePlan()
    restoreFocus()
  }
  const onClose = () => {
    hideModal()
    restoreFocus()
  }
  const onPrimaryChanged = (primaryPlanId: string) => {
    setNewPrimaryPlanId(primaryPlanId)
    onConfirm()
  }

  // Determine if the current plan is the primary and if there are other plans
  let planIsPrimary = false
  let hasMultiplePlans = false
  let primaryPlan
  if (plansByOwnerQuery.data && plansByOwnerQuery.data.plans) {
    const {
      data: { plans }
    } = plansByOwnerQuery
    primaryPlan = plans.find((p) => p.isPrimary)
    planIsPrimary = primaryPlan?.id === id
    const otherPlans = plans.filter((p) => p.id !== id)
    hasMultiplePlans = otherPlans.length >= 1
  }

  if (!mutation.called) {
    if (planIsPrimary && hasMultiplePlans) {
      return (
        <ConfirmReplacePrimaryModal
          id={id}
          bodyText="To delete this plan, please designate another plan as your primary plan."
          confirmText="Delete Plan"
          isOpen={isOpen}
          onClose={onClose}
          onMutationCompleted={onPrimaryChanged}
          ownerAppUserId={ownerAppUserId}
          title="Delete Plan"
          updateCache={false}
        />
      )
    }
    return <ConfirmDeletePlanModal isOpen={isOpen} onClose={onClose} onConfirm={onConfirm} />
  }

  if (user && ownerAppUserId && user.appUserId !== ownerAppUserId) {
    // if deleted by non-owner, redirect to owner's primary plan in StudentOverview.
    if (primaryPlan) {
      return <Redirect to={`/students/${ownerAppUserId}/plans/${primaryPlan.id}`} />
    }
    // if no primary plan is found, user has no plans. Redirect to NoPlans.
    return <Redirect to={`/students/${ownerAppUserId}/plans`} />
  }
  // if deleted by owner, redirect to plans page
  return <Redirect to="/plans" />
}

export default ConfirmDeletePlanModalWrapper
